/* ***************************************************************** */
/* TODO: eslint pre-existing exceptions auto generated on 2022-09-08 */
/*       Do not add to these rule exceptions. Only remove as the     */
/*       violations are cleaned from our code.                       */
/*                                                                   */
/*       eslint-disable no-shadow                                    */
/* ***************************************************************** */
import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Box, Button } from '@mui/material';
import { Vega, VegaLite } from 'react-vega';
import { buildBarChartVegaSpec } from '@advanced-agrilytics/terraframing-chart-builder';
import { isEqual } from 'lodash-es';

import Adjustments from './GroupAdjustment';
import {
  getFungicidePreview,
  saveFungicideAdjustments,
} from '../../../lib/api';
import { getFormattedStats } from '../../../lib/charts';
import { getRounded } from '../../../lib/numbers';
import useDebounce from '../../../hooks/useDebounce';
import { usePermission } from '../../../hooks/use-permission';
import { useMapRefresh } from '../../../components/google/maps/use-map-refresh';
import { getChartColors } from '../../../services/chart-colors';

ProductDetails.propTypes = {
  stats: PropTypes.object.isRequired,
  setActions: PropTypes.func.isRequired,
  onChange: PropTypes.func.isRequired,
};

export default function ProductDetails(props) {
  const { setActions, onChange } = props;
  const canAdjust = usePermission('RUN_RX');
  const [isWorking, setWorking] = useState(false);
  const [shouldRefreshMap, setShouldRefreshMap] = useState(false);
  const [stats, setStats] = useState(props.stats);
  const [fullFieldAdjustment, setFullFieldAdjustment] = useState(
    props.stats.fullFieldAdjustment || '',
  );
  const [groupAdjustments, setGroupAdjustments] = useState(
    props.stats.groupAdjustments,
  );
  const debouncedAdjustments = useDebounce(groupAdjustments, 200);
  const { refresh: refreshMap } = useMapRefresh();

  useEffect(refreshMapEffect, [shouldRefreshMap, refreshMap]);

  useEffect(previewAdjustmentsEffect, [
    debouncedAdjustments,
    stats.aaid,
    stats.key,
  ]);

  useEffect(setActionsEffect, [
    canAdjust,
    fullFieldAdjustment,
    groupAdjustments,
    isWorking,
    onChange,
    props.stats.distribution,
    setActions,
    stats,
  ]);

  function refreshMapEffect() {
    if (!shouldRefreshMap) return;

    setShouldRefreshMap(() => {
      refreshMap();
      return false;
    });
  }

  function previewAdjustmentsEffect() {
    getFungicidePreview(stats.aaid, stats.key, debouncedAdjustments).then(
      (preview) => {
        setStats(preview);
      },
    );
  }

  function setActionsEffect() {
    if (canAdjust)
      setActions([
        <Button
          key="revert"
          onClick={handleRevert}
          disabled={isWorking || !groupAdjustments?.length}
          variant="contained"
        >
          Revert
        </Button>,
        <Button
          key="save"
          onClick={handleSave}
          disabled={
            isWorking || isEqual(stats.distribution, props.stats.distribution)
          }
          variant="contained"
        >
          Save
        </Button>,
      ]);

    function handleSave() {
      setWorking(true);
      saveFungicideAdjustments(stats.aaid, stats.key, {
        groupAdjustments,
        fullFieldAdjustment,
      })
        .then((stats) => onChange(stats))
        .finally(() => {
          setWorking(false);
          setShouldRefreshMap(true);
        });
    }
  }

  return (
    <>
      {buildHeader()}
      {buildBarChartAndMap()}
      {buildAdjustments()}
    </>
  );

  function buildHeader() {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-around',
        }}
      >
        <h3>Min: {getRounded(stats.stats.min)}oz</h3>
        <h3>Max: {getRounded(stats.stats.max)}oz</h3>
        <h3>Avg: {getRounded(stats.stats.avg)}oz</h3>
        <h3>Total: {getRounded(stats.stats.total)}oz</h3>
      </Box>
    );
  }

  function buildBarChartAndMap() {
    const colors = getChartColors(`fungicide-${stats.product}`);
    const formattedStats = getFormattedStats(stats, colors);

    return (
      <Box sx={{ display: 'flex' }}>
        <Box sx={{ flex: '1' }}>{buildBarChart(formattedStats)}</Box>
        <Box>{buildMap(formattedStats)}</Box>
      </Box>
    );
  }

  function buildBarChart(stats) {
    const spec = {
      ...buildBarChartVegaSpec({
        maxBars: 28,
        data: stats.chart.values.map(({ label: x, value: y }) => ({ x, y })),
        colors: stats.chart.values.reduce(
          (colors, { label }, i) => ({
            ...colors,
            [Math.round(label)]: rgbaToArray(stats.colors[i]),
          }),
          {},
        ),
      }),
      height: 180,
      width: 500,
    };

    return <Vega spec={spec} />;
  }

  function buildMap(stats) {
    const domain = stats.chart.values.map((cv) => cv.label);
    const range = stats.colors.map((c) => rgbaToHex(c));
    const spec = {
      height: 215,
      width: 200,
      mark: { type: 'geoshape' },
      projection: { type: 'mercator' },
      data: {
        values: { type: 'FeatureCollection', features: stats.features },
        format: { type: 'json', property: 'features' },
      },
      encoding: {
        color: {
          type: 'quantitative',
          field: 'properties.TGT_RATE',
          title: 'Rate ozs/ac',
          scale: { domain, range, type: 'linear' },
        },
      },
    };

    return <VegaLite spec={spec} />;
  }

  function handleFullFieldAdjustment(adjustment) {
    setFullFieldAdjustment(adjustment);
    setGroupAdjustments(
      stats.baseDistribution.map(({ label }) => ({
        minvalue: label,
        adjustmentFactor: Number(adjustment),
      })),
    );
  }

  function handleGroupAdjustment(groupValue, adjustmentFactor) {
    setGroupAdjustments([
      ...groupAdjustments.filter((a) => a.minvalue !== groupValue),
      { minvalue: groupValue, adjustmentFactor },
    ]);
  }

  function handleRevert() {
    setFullFieldAdjustment('');
    setGroupAdjustments([]);
  }

  function buildAdjustments() {
    if (!canAdjust) return null;

    return (
      <Adjustments
        maxRate={stats.maxApplicationRate}
        groupBreaks={stats.baseDistribution.map((s) => parseFloat(s.label))}
        groupAdjustments={groupAdjustments}
        fullFieldAdjustment={fullFieldAdjustment}
        onGroupChange={handleGroupAdjustment}
        onFullFieldChange={handleFullFieldAdjustment}
      />
    );
  }
}

function rgbaToArray(rgba) {
  return /\((.*)\)/
    .exec(rgba)[1]
    .split(',')
    .map((c) => Number(c));
}

function rgbaToHex(rgba) {
  const extractionRegEx = /\(([^)]+)\)/;
  const numbers = extractionRegEx.exec(rgba);
  if (!numbers[0]) return '#777777'; // Default to return gray.
  const trimmedNumbers = numbers[0].substring(1, numbers[0].length - 1);
  const splits = trimmedNumbers.split(',').map((sp) => sp.replace(/ /g, ''));
  const numericSplits = splits.map((numStr) => parseInt(numStr, 10));
  const [r, g, b] = numericSplits;
  let rHex = r.toString(16);
  rHex = rHex.length === 1 ? `0${rHex}` : rHex;
  let gHex = g.toString(16);
  gHex = gHex.length === 1 ? `0${gHex}` : gHex;
  let bHex = b.toString(16);
  bHex = bHex.length === 1 ? `0${bHex}` : bHex;
  return `#${rHex}${gHex}${bHex}`;
}
